import React, { useState, useMemo, useCallback } from 'react';
import VideoCard from './VideoCard';
import { MockVideo } from '@/data/mockVideoData';
import DashboardCard from '@/components/ui/DashboardCard';
import VideoLibraryHeader from './VideoLibraryHeader';
import { getMockVideos, getFavoriteVideos } from '@/data/mockVideoData';

interface VideoLibraryContainerProps {
  styleId?: string | null;
  influenceId?: string | null;
  backgroundId?: string | null;
  environmentIds?: string[];
  moodIds?: string[];
  colorIds?: string[];
  hasAudio?: boolean;
  description?: string;
  debouncedDescription?: string;
  selectedVideo?: MockVideo;
  onVideoSelect?: (video: MockVideo) => void;
}

// Stable filter types
type FilterType = 'all' | 'user' | 'favorites';

// Stable filter functions - created once and reused
const FILTER_FUNCTIONS = Object.freeze({
  all: (videos: readonly MockVideo[]) => videos,
  user: (videos: readonly MockVideo[]) => videos.filter(v => ['1', '3', '5', '18'].includes(v.id)),
  favorites: (videos: readonly MockVideo[]) => videos.filter(v => v.isLiked)
});

// Stable audio filter function
const applyAudioFilter = (videos: readonly MockVideo[], hasAudio?: boolean) => {
  if (hasAudio === undefined) return videos;
  return videos.filter(video => video.hasAudio === hasAudio);
};

// Memoized video card component to prevent unnecessary re-renders
const MemoizedVideoCard = React.memo(VideoCard, (prevProps, nextProps) => {
  return (
    prevProps.video.id === nextProps.video.id &&
    prevProps.video.isLiked === nextProps.video.isLiked &&
    prevProps.video.likeCount === nextProps.video.likeCount &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.onLike === nextProps.onLike &&
    prevProps.onNavigate === nextProps.onNavigate
  );
});

export const VideoLibraryContainer: React.FC<VideoLibraryContainerProps> = ({
  styleId,
  influenceId,
  backgroundId,
  environmentIds,
  moodIds,
  colorIds,
  hasAudio,
  description,
  debouncedDescription,
  selectedVideo,
  onVideoSelect
}) => {
  // Single state for library filters
  const [libraryFilter, setLibraryFilter] = useState<FilterType>('all');
  
  // Memoized base videos - stable reference
  const baseVideos = useMemo(() => getMockVideos(), []);
  
  // Memoized filtered videos with stable reference
  const filteredVideos = useMemo(() => {
    // Apply audio filter first
    const audioFiltered = applyAudioFilter(baseVideos, hasAudio);
    
    // Apply library filter
    const filterFn = FILTER_FUNCTIONS[libraryFilter];
    return filterFn(audioFiltered);
  }, [baseVideos, hasAudio, libraryFilter]);
  
  // Stable handlers to prevent unnecessary re-renders
  const handleVideoSelect = useCallback((video: MockVideo) => {
    if (onVideoSelect) {
      onVideoSelect(video);
    }
    console.log('Selected video:', video);
  }, [onVideoSelect]);

  const handleVideoNavigate = useCallback((video: MockVideo) => {
    handleVideoSelect(video);
  }, [handleVideoSelect]);

  const handleVideoLike = useCallback((video: MockVideo) => {
    console.log('Toggle like for video:', video.id);
    // Note: In real implementation, this would update the backend
    // For now, we'll just log the action to prevent state mutation
  }, []);

  // Stable filter change handlers
  const handleShowOnlyUserVideos = useCallback((show: boolean) => {
    setLibraryFilter(show ? 'user' : 'all');
  }, []);

  const handleShowFavorites = useCallback((show: boolean) => {
    setLibraryFilter(show ? 'favorites' : 'all');
  }, []);

  // Memoized header props to prevent unnecessary re-renders
  const headerProps = useMemo(() => ({
    showOnlyUserVideos: libraryFilter === 'user',
    showFavorites: libraryFilter === 'favorites',
    onSetShowOnlyUserVideos: handleShowOnlyUserVideos,
    onSetShowFavorites: handleShowFavorites,
    isLoading: false
  }), [libraryFilter, handleShowOnlyUserVideos, handleShowFavorites]);

  // Memoized empty state message
  const emptyStateMessage = useMemo(() => {
    if (libraryFilter === 'user') {
      return {
        title: 'No videos found',
        description: 'No videos found in your creations. Generate some videos to see them here!'
      };
    }
    if (libraryFilter === 'favorites') {
      return {
        title: 'No favorite videos found',
        description: 'Like some videos to see them here!'
      };
    }
    if (hasAudio !== undefined) {
      return {
        title: 'No videos found',
        description: `No videos ${hasAudio ? 'with' : 'without'} audio found. Try adjusting your filters.`
      };
    }
    return {
      title: 'No videos found',
      description: 'Start by generating your first video!'
    };
  }, [libraryFilter, hasAudio]);

  // Early return for empty state
  if (filteredVideos.length === 0) {
    return (
      <DashboardCard variant="purple" className="p-3 md:p-6">
        <VideoLibraryHeader {...headerProps} />
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <div className="text-muted-foreground text-center">
            <h3 className="text-lg font-medium mb-2">{emptyStateMessage.title}</h3>
            <p className="text-sm">{emptyStateMessage.description}</p>
          </div>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard variant="purple" className="p-3 md:p-6">
      <VideoLibraryHeader {...headerProps} />

      {/* Video Grid - Optimized with stable references */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredVideos.map((video) => (
          <MemoizedVideoCard
            key={video.id}
            video={video}
            onSelect={handleVideoSelect}
            onLike={handleVideoLike}
            allVideos={Array.from(filteredVideos)}
            onNavigate={handleVideoNavigate}
          />
        ))}
      </div>

      {/* Pagination placeholder - Memoized */}
      <div className="flex justify-center pt-4">
        <div className="text-sm text-muted-foreground">
          Showing {filteredVideos.length} of {filteredVideos.length} videos
        </div>
      </div>
    </DashboardCard>
  );
}; 